import React, { useContext, useState } from "react";
import { Alert, Button, Form } from "react-bootstrap";
import { useLocation } from "react-router-dom";

import { Context } from "../../../index";
import { resolveLang, withLang } from "../../../i18n/routesLang";

import ToolSeoPageTemplate from "../../../components/seo/seoTemplates/ToolSeoPageTemplate";
import { buildSeoPayload } from "../../../components/seo/buildSeoPayload/buildSeoPayload";

import { pickContactPageT } from "./Contact_Page.lang";

import { CONTACT_ROUTE } from "../../../utils/consts";
import { sendEmailFromContactPage } from "../../../http/bookAPI";

const ContactSupportTool = ({ user }) => {
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    const [status, setStatus] = useState(null);
    const [sending, setSending] = useState(false);

    const account = user?.user || {};
    const name = account.username || account.amz_name || account.email || "Virtalibry user";
    const email = account.email || "";
    const shellStyle = {
        maxWidth: 520,
        margin: "14px auto 10px",
        border: "1px solid #6f7f8d",
        borderRadius: 6,
        padding: "26px 24px 22px",
        background: "#fff",
        boxShadow: "0 6px 14px rgba(20, 35, 28, 0.08)",
    };
    const buttonStyle = {
        minHeight: 36,
        border: "1px solid #0c7b65",
        borderRadius: 6,
        padding: "7px 18px",
        background: "linear-gradient(180deg, #086f5c, #004f45)",
        boxShadow: "0 10px 24px rgba(0, 79, 69, 0.18)",
        fontWeight: 600,
    };

    if (!user?.isAuth) {
        return (
            <div className="text-center" style={shellStyle}>
                <h2 className="h5 text-muted mb-2">Need help? Contact support</h2>
                <p className="text-muted mb-3">Support messages are available only to registered Virtalibry users.</p>
                <a className="btn btn-success" href="/registration" style={buttonStyle}>
                    <i className="fa-solid fa-user-plus me-2" aria-hidden="true"></i>
                    Sign Up
                </a>
            </div>
        );
    }

    const handleSubmit = async (event) => {
        event.preventDefault();
        setStatus(null);

        if (!subject.trim() || !message.trim()) {
            setStatus({ type: "warning", text: "Please enter a subject and message." });
            return;
        }

        if (!email) {
            setStatus({ type: "danger", text: "Your account email is missing. Please update your profile or contact support directly." });
            return;
        }

        try {
            setSending(true);
            await sendEmailFromContactPage({
                name,
                email,
                subject: subject.trim(),
                message: message.trim(),
            });
            setSubject("");
            setMessage("");
            setStatus({ type: "success", text: "Message sent. We will reply to your account email." });
        } catch (error) {
            setStatus({
                type: "danger",
                text: error?.response?.data?.message || "Could not send the message. Please try again.",
            });
        } finally {
            setSending(false);
        }
    };

    return (
        <div style={shellStyle}>
            <div className="text-center mb-3">
                <h2 className="h5 text-muted mb-1">Need help? Contact support</h2>
                <p className="text-muted small mb-0">Send a message from your registered Virtalibry account.</p>
            </div>

            {status && <Alert variant={status.type}>{status.text}</Alert>}

            <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3" controlId="contactSupportSubject">
                    <Form.Label className="fw-semibold text-muted">Subject</Form.Label>
                    <Form.Control
                        value={subject}
                        onChange={(event) => setSubject(event.target.value)}
                        maxLength={200}
                        placeholder="What do you need help with?"
                    />
                </Form.Group>

                <Form.Group className="mb-3" controlId="contactSupportMessage">
                    <Form.Label className="fw-semibold text-muted">Message</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={5}
                        value={message}
                        onChange={(event) => setMessage(event.target.value)}
                        maxLength={10000}
                        placeholder="Describe the issue or question."
                    />
                </Form.Group>

                <div className="text-center">
                    <Button type="submit" variant="success" disabled={sending} style={buttonStyle}>
                        <i className="fa-solid fa-paper-plane me-2" aria-hidden="true"></i>
                        {sending ? "Sending..." : "Send Message"}
                    </Button>
                </div>
            </Form>
        </div>
    );
};

const ContactPage = () => {
    const { user } = useContext(Context);
    const location = useLocation();

    const lang = resolveLang({
        pathname: location.pathname,
        storeLang: user?.lang,
    });

    const t = pickContactPageT(lang);

    const PATH = CONTACT_ROUTE || "/contact";
    const pageUrl = `https://virtalibry.com${withLang(PATH, lang)}`;

    const seo = buildSeoPayload({ t, pageUrl });

    return (
        <ToolSeoPageTemplate
            seo={seo}
            hero={t.hero}
            blocks={t.blocks}
            textMode="contact"
            tool={<ContactSupportTool user={user} />}
            lang={lang}
            pageKey="contact"
        />
    );
};

export default ContactPage;
